// import node module libraries
import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Row, Col, Button, Dropdown, Badge, Table, Offcanvas, Form } from 'react-bootstrap';
import { MoreVertical, Edit, Trash, HelpCircle } from 'react-feather';
import Moment from 'react-moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import required custom components
import TanstackTable from 'components/elements/advance-table/TanstackTable';
// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import the new components
import OffcanvasUpdateGuide from './OffcanvasUpdateGuide';
import ModalDeleteGuide from './ModalDeleteGuide';
import { guid } from '@fullcalendar/core/internal';

const GuideSettingsTable = ({ activeKey, guideData, isLoadingTable, filterBy = '' }) => {
	// Define required states
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [selectedRow, setSelectedRow] = useState({});
	const [selectedGuideCateory, setSelectedGuideCateory] = useState({});

	// Methods / Functions
	const handleRowClick = (row, action) => {
		if (action === 'open') {
			setShowUpdateGuideOffcanvas(true);
			fetchRowDetails(row); // Call API to fetch log details
		} else if (action === 'delete') {
			handleDeleteClick(row);
			setShowUpdateGuideOffcanvas(false);
		}
	};

	// Update Guide
	const [isLoadingButtonUpdateGuide, setIsLoadingButtonUpdateGuide] = useState(null); // State for loading spinner
	const [showUpdateGuideOffcanvas, setShowUpdateGuideOffcanvas] = useState(false);

	const handleCloseUpdateGuide = () => {
		setShowUpdateGuideOffcanvas(false);
		setSelectedRow({});
	};

	const handleUpdateGuide = async (e) => {
		e.preventDefault();
		setIsLoadingButtonUpdateGuide(true);

		const data = {
			guide_title: e.target.title.value,
			guide_slug: e.target.title_slug.value,
			guide_content: e.target.content.value,
			guide_category_id: selectedGuideCateory ? selectedGuideCateory.id : null // Use the selected category ID
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/public/guides/${e.target.id.value}`, data, options)
			.then(response => {
				// Update the selected guide in the data state
				const updatedGuide = response.data;

				setSelectedRow(updatedGuide.result); // Update selectedRow state with fetched details
				// Update the GuideData state with the updated row
				const updatedData = guideData.data.map((row) => 
					row.id === updatedGuide.result.id
						? {
							...row, // Keep all existing keys and values
							guide_title: updatedGuide.result.guide_title,
							guide_slug: updatedGuide.result.guide_slug,
							guide_content: updatedGuide.result.guide_content,
							guide_category_id: selectedGuideCateory ? selectedGuideCateory.value : null, // Use the selected category ID
							guide_category_title: selectedGuideCateory ? selectedGuideCateory.label : null // Use the selected category label
						}
						: row
				);

				guideData.data = updatedData;
				// Close the offcanvas
				setShowUpdateGuideOffcanvas(false);
				setIsLoadingButtonUpdateGuide(false);
				// Reset selected row
				setSelectedRow({});
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setShowUpdateGuideOffcanvas(true);
				setIsLoadingButtonUpdateGuide(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	// Delete Guide
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const [rowToDelete, setRowToDelete] = useState(null); // State for API Credential to delete
	const [isLoadingButtonDeleteGuide, setIsLoadingButtonDeleteGuide] = useState(null); // State for loading spinner

	const handleDeleteClick = (guide) => {
		setRowToDelete(guide);
		setShowDeleteModal(true);
	};

	// fetch data from api
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const handleConfirmDelete = async () => {
		if (rowToDelete) {
			setIsLoadingButtonDeleteGuide(true);
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			await axios.delete(`${baseURL}/public/guides/${rowToDelete}`, options)
				.then(response => {
					// Update the quidedata state with the deleted row
					const updatedData = guideData.data.filter((row) => row.id !== rowToDelete);
					guideData.data = updatedData;
					// Update the quidedata state with the deleted data
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteGuide(false);
					handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
				})
				.catch((error) => {
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteGuide(false);
					const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
					handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				});
		}
	};

	const [showUpdateGuideCategoryOffcanvas, setShowUpdateGuideCategoryOffcanvas] = useState(false);

	const fetchRowDetails = async (rowId) => {
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Fetch the guide details
		try {
			const response = await axios.get(`${baseURL}/public/guides/${rowId}`, options);
			const rowDetails = response.data;
			setSelectedRow(rowDetails.data); // Update selectedRow state with fetched details

		} catch (error) {
			console.error(error);
		}
	};
	
	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to="#"
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	// 
	// Components
	//

	// Component ActionMenu
	const ActionMenu = (row) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('ACTION')}</Dropdown.Header>
					<Dropdown.Item eventKey="1" onClick={() => handleRowClick(row.ID, 'open')}>
						<Edit size="14px" className="me-1" /> {t('Edit')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="2" onClick={() => handleRowClick(row.ID, 'delete')}>
						<Trash size="14px" className="me-1" /> {t('Delete')}
					</Dropdown.Item>
					<Dropdown.Divider />
					<Dropdown.Header>{t('HELP CENTER')}</Dropdown.Header>
					<Dropdown.Item eventKey="3" target="_blank" href="/marketing/help-center/guide/">
						<HelpCircle size="14px" className="me-1" /> {t('Open in Help Center')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	const unfilteredData = useMemo(() => guideData.data ?? [], [guideData.data]);
	//const pagination = useMemo(() => response?.pagination ?? [], [response.pagination]);

	// filtering....
	const data = useMemo(() => {
		return filterBy === '' 
			? unfilteredData 
			: unfilteredData?.filter((item) => item.guide_category_slug?.includes(filterBy));
	}, [filterBy, unfilteredData]);;
	
	const columns = useMemo(
		() => [
			{
				header: t('Title'),
				cell: ({ row }) => {
					return (
						<Link className="fw-semibold" onClick={(event) => {
							event.preventDefault();
							handleRowClick(row.original.id, 'open');
						}}>
							{row.original.guide_title.toString()} <i className="fe fe-arrow-right fs-4"></i>
						</Link>
					);
				}
			},
			{
				header: t('Category'),
				cell: ({ row }) => {
					return (
						<Badge className="me-1 text-light-secondary bg-secondary">
							{row.original.guide_category_title ? row.original.guide_category_title : row.original.guide_category_id}
						</Badge>
					);
				}

			},
			{
				header: t('Feedback'),
				cell: ({ row }) => (
					<Row>
						<div className="">
							<h5 className="mb-n1">{row.original.guide_helpful_yes ? row.original.guide_helpful_yes : '0'}< i className="fe fe-thumbs-up mx-1"></i ></h5>
							<small>{t('helpful')}</small>
						</div>
						<div className="mt-2">
							<h5 className="mb-n1">{row.original.guide_helpful_no ? row.original.guide_helpful_no : '0'}< i className="fe fe-thumbs-down mx-1"></i ></h5>
							<small>{t('not helpful')}</small>
						</div>
					</Row>
				),
			},
			{
				accessorKey: 'guide_last_update',
				header: t('Last updated at'),
				cell: ({ getValue }) => (
					<Moment format="DD.MM.YYYY" date={getValue()} />
				),
			},
			{
				accessorKey: 'action',
				header: t('Action'),
				cell: ({ row }) => {
					return <ActionMenu ID={row.original.id} />;
				}
			}
		],
		[]
	);

	if (isLoadingTable) {
		return (
			<Table className="text-nowrap">
				<thead >
					<tr>
						<th scope="col">{t('Title')}</th>
						<th scope="col">{t('Category')}</th>
						<th scope="col">{t('Feedback')}</th>
						<th scope="col">{t('Last updated at')}</th>
						<th scope="col">{t('Action')}</th>
					</tr>
				</thead>
				<tbody>
					{Array(10).fill(0).map((_, index) => (
						<tr key={index}>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
						</tr>
					))}
				</tbody>
			</Table>

		);
	}

	return (
		<div>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<TanstackTable
				data={data}
				columns={columns}
				filter={true}
				filterPlaceholder="Suche"
				pagination={true}
				exportButton={false}

			/>
			{/* Update Guide Offcanvas */}
			{selectedRow && Object.keys(selectedRow).length > 0 && (
				<OffcanvasUpdateGuide
					show={showUpdateGuideOffcanvas}
					onHide={handleCloseUpdateGuide}
					selectedRow={selectedRow}
					onSubmit={handleUpdateGuide}
					isLoadingButton={isLoadingButtonUpdateGuide}
					selectedGuideCategory={selectedGuideCateory} // Pass selectedGuideCategory as prop
					setSelectedGuideCategory={setSelectedGuideCateory} // Pass setSelectedGuideCategory as prop
				/>
			)}
			{/* Delete Guide Modal */}
			<ModalDeleteGuide
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				handleConfirmDelete={handleConfirmDelete}
				isLoadingButton={isLoadingButtonDeleteGuide}
			/>
		</div>
	);
};

export default GuideSettingsTable;
